import { useState, useEffect } from "react";
import { Button } from "@workspace/web-ui/components/button";
import { Dialog, DialogContent, DialogTitle } from "@workspace/web-ui/components/dialog";
import { Input } from "@workspace/web-ui/components/input";
import { Label } from "@workspace/web-ui/components/label";
import { Skeleton } from "@workspace/web-ui/components/skeleton";
// import { Image } from "@workspace/web-ui/components/image";
import { useApproveVerificationMutation, useRejectVerificationMutation } from "../../api/mutations";
import { VerificationResponse } from "../../api/queries";
import { CheckCircle2, XCircle, FileText, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import NoPreviewCard from "../card/no-preview-card";

interface VerificationProps {
    verificationData: VerificationResponse | null;
    userId: string;
}

export const Verification = ({ verificationData, userId }: VerificationProps) => {
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [idImageLoading, setIdImageLoading] = useState(true);
    const [photoImageLoading, setPhotoImageLoading] = useState(true);
    
    const approveMutation = useApproveVerificationMutation();
    const rejectMutation = useRejectVerificationMutation();

    const verification = verificationData?.verification;
    console.log("veridication url", verification?.id_url)
    console.log("veridication photo url", verification?.photo_url)

    // Reset loading states when URLs change
    useEffect(() => {
        if (verification?.id_url) {
            setIdImageLoading(true);
        }
    }, [verification?.id_url]);

    useEffect(() => {
        if (verification?.photo_url) {
            setPhotoImageLoading(true);
        }
    }, [verification?.photo_url]);

    const handleApprove = async () => {
        try {
            await approveMutation.mutateAsync({ userId });
        } catch (error) {
            // Error is handled by the mutation
        }
    };

    const handleReject = async () => {
        if (!rejectReason.trim()) {
            return;
        }
        try {
            await rejectMutation.mutateAsync({ userId, reason: rejectReason });
            setRejectModalOpen(false);
            setRejectReason("");
        } catch (error) {
            // Error is handled by the mutation
        }
    };

    const getStatusBadge = (status: string, verified: boolean) => {
        if (verified) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle2 className="w-3 h-3" />
                    Verified
                </span>
            );
        }
        if (status === "failed") {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle className="w-3 h-3" />
                    Rejected
                </span>
            );
        }
        if (status === "submitted") {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <AlertCircle className="w-3 h-3" />
                    Pending Review
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {status}
            </span>
        );
    };

    if (!verification) {
        return (
            <NoPreviewCard
                Icon={FileText}
                title="No Verification Found"
                description="This patient has not submitted any identity verification documents."
                cardTitle="Identity Verification"
            />
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-[#E6E6E6]">
                <div className="px-4 sm:px-6 py-4 border-b border-[#DCDCDE]">
                    <h2 className="text-lg font-medium text-[#2D2F35]">Identity Verification</h2>
                </div>
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                    {/* Status */}
                    <div className="flex flex-row items-center justify-between gap-2">
                        <span className="text-sm font-medium text-[#2D2F35]">Status</span>
                        {getStatusBadge(verification.status, verification.verified)}
                    </div>

                    {/* ID Type */}
                    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-y-1 gap-x-4">
                        <span className="font-medium text-sm text-[#2D2F35]">ID Type:</span>
                        <span className="text-sm text-[#464B55]">{verification.id_type || "N/A"}</span>
                    </div>

                    {/* Method */}
                    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-y-1 gap-x-4">
                        <span className="font-medium text-sm text-[#2D2F35]">Method:</span>
                        <span className="text-sm text-[#464B55] capitalize">{verification.method || "N/A"}</span>
                    </div>

                    {/* Submitted On */}
                    {verification.submitted_on && (
                        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-y-1 gap-x-4">
                            <span className="font-medium text-sm text-[#2D2F35]">Submitted On:</span>
                            <span className="text-sm text-[#464B55]">
                                {format(new Date(verification.submitted_on), "MMM dd, yyyy 'at' h:mm a")}
                            </span>
                        </div>
                    )}

                    {/* Verified On */}
                    {verification.verified_on && (
                        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-y-1 gap-x-4">
                            <span className="font-medium text-sm text-[#2D2F35]">Verified On:</span>
                            <span className="text-sm text-[#464B55]">
                                {format(new Date(verification.verified_on), "MMM dd, yyyy 'at' h:mm a")}
                            </span>
                        </div>
                    )}

                    {/* Rejected On */}
                    {verification.rejected_on && (
                        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-y-1 gap-x-4">
                            <span className="font-medium text-sm text-[#2D2F35]">Rejected On:</span>
                            <span className="text-sm text-[#464B55]">
                                {format(new Date(verification.rejected_on), "MMM dd, yyyy 'at' h:mm a")}
                            </span>
                        </div>
                    )}

                    {/* Auto Rejection Reason - Only show if not verified */}
                    {verification.auto_rejection_reason && (
                        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-y-1 gap-x-4">
                            <span className="font-medium text-sm text-[#2D2F35]">Rejection Reason:</span>
                            <span className="text-sm text-[#464B55]">{verification.auto_rejection_reason}</span>
                        </div>
                    )}

                    {/* Manual Rejection Reason - Only show if not verified */}
                    {verification?.manual_rejection_reason && (
                        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-y-1 gap-x-4">
                            <span className="font-medium text-sm text-[#2D2F35]">Doctor's Rejection Reason:</span>
                            <span className="text-sm text-[#464B55]">{verification.manual_rejection_reason}</span>
                        </div>
                    )}

                    {/* ID Image */}
                    {verification.id_url && (
                        <div className="space-y-2">
                            <span className="font-medium text-sm text-[#2D2F35]">ID Document</span>
                            <div className="border border-[#E6E6E6] rounded-lg p-2 sm:p-4 bg-gray-50 relative">
                                {idImageLoading && (
                                    <Skeleton className="w-full h-64 sm:h-96 rounded-lg absolute inset-2 sm:inset-4" />
                                )}
                                <img
                                    src={verification.id_url}
                                    alt="ID Document"
                                    className={`w-full h-auto rounded-lg object-contain max-h-64 sm:max-h-96 transition-opacity duration-300 ${
                                        idImageLoading ? "opacity-0" : "opacity-100"
                                    }`}
                                    onLoad={() => setIdImageLoading(false)}
                                    onError={() => setIdImageLoading(false)}
                                />
                            </div>
                        </div>
                    )}

                    {/* Photo Image */}
                    {verification.photo_url && (
                        <div className="space-y-2">
                            <span className="font-medium text-sm text-[#2D2F35]">Photo</span>
                            <div className="border border-[#E6E6E6] rounded-lg p-2 sm:p-4 bg-gray-50 relative">
                                {photoImageLoading && (
                                    <Skeleton className="w-full h-64 sm:h-96 rounded-lg absolute inset-2 sm:inset-4" />
                                )}
                                <img
                                    src={verification.photo_url}
                                    alt="Patient Photo"
                                    className={`w-full h-auto rounded-lg object-contain max-h-64 sm:max-h-96 transition-opacity duration-300 ${
                                        photoImageLoading ? "opacity-0" : "opacity-100"
                                    }`}
                                    onLoad={() => setPhotoImageLoading(false)}
                                    onError={() => setPhotoImageLoading(false)}
                                />
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    {!verification.verified && verification.status === "submitted" && (
                        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-[#E6E6E6] w-full">
                            <Button
                                onClick={handleApprove}
                                disabled={approveMutation.isPending}
                                className="button-primary !bg-[#00C781] hover:!bg-[#059669] !text-white flex gap-2 items-center justify-center !shadow-none !border-none h-[44px] w-full sm:flex-[2]"
                            >
                                {approveMutation.isPending ? (
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                    </svg>
                                ) : (
                                    <CheckCircle2 className="w-4 h-4" />
                                )}
                                Approve Verification
                            </Button>
                            <Button
                                onClick={() => setRejectModalOpen(true)}
                                disabled={rejectMutation.isPending}
                                className="button-primary !bg-[#DC2626] !text-white flex gap-2 items-center justify-center !shadow-none !border-none h-[44px] w-full sm:flex-1"
                            >
                                <XCircle className="w-4 h-4" />
                                Reject Verification
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Reject Modal */}
            <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
                <DialogContent className="w-full max-w-[450px] flex flex-col p-0 overflow-hidden">
                    <DialogTitle></DialogTitle>
                    <div className="shrink-0 px-4 sm:px-6 pt-6 pb-3">
                        <h2 className="text-xl sm:text-2xl font-medium">Reject Verification</h2>
                        <p className="text-sm text-gray-500 mt-1">Please provide a reason for rejecting this verification</p>
                    </div>
                    <div className="flex-1 min-h-0 px-4 sm:px-6 pb-6 space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="reject-reason">Rejection Reason</Label>
                            <Input
                                id="reject-reason"
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Enter reason for rejection..."
                                className="w-full"
                            />
                        </div>
                        <div className="flex gap-3 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setRejectModalOpen(false);
                                    setRejectReason("");
                                }}
                                className="button-primary w-full"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                onClick={handleReject}
                                disabled={!rejectReason.trim() || rejectMutation.isPending}
                                className="button-primary !bg-red-600 !text-white w-full !border-none"
                            >
                                {rejectMutation.isPending ? (
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                    </svg>
                                ) : (
                                    "Reject"
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

